import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import Header from './Header';
import CourseCard from './CourseCard';
import Tag from './Tag';
import ProbeterminButton from './ProbeterminButton';
import Popup from './Popup';
import Images from '../assets/Images';
import Footer from './Footer';
import theme, { mobileBreakpoint } from '../theme';
import texts from '../locales/texts';
import PageLayout from './PageLayout';
import { Link } from 'react-router-dom';
import { StarBadgeWithIcon } from './shared/CommonElements';
import { useAuth } from '../AuthContext';
import config from '../config';

// Keyframes for CTA button animations
const sparkle = keyframes`
  0%, 100% { opacity: 0; transform: scale(0) rotate(0deg); }
  50% { opacity: 1; transform: scale(1) rotate(180deg); }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 4px 20px rgba(219, 39, 98, 0.4), 0 0 20px rgba(241, 222, 124, 0.3); }
  50% { box-shadow: 0 6px 30px rgba(219, 39, 98, 0.6), 0 0 30px rgba(241, 222, 124, 0.5); }
`;

const PageContainer = styled.div`
  background-color: ${props => props.theme.colors.background};
  min-height: 100vh;
  padding: 20px;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px 0;
  padding-left: 20px;
  padding-right: 20px;
`;

const HeroSection = styled.section`
  position: relative;
  display: flex;
  justify-content: space-between;
  margin: 40px 0;
  
  @media (max-width: ${mobileBreakpoint}px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const HeroContent = styled.div`
  max-width: 600px;
`;

const HeroTitle = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;



const MainTitle = styled.h1`
  font-size: 64px;
  margin: 0;
  line-height: 1;
  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 48px;
  }
`;

const Subtitle = styled.h2`
  font-size: 64px;
  font-style: italic;
  margin: 0 0 24px 0;
  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 48px;
  }
`;

const Description = styled.p`
  font-size: 18px;
  line-height: 1.6;
  margin-bottom: 32px;
  max-width: 500px;
`;

const CoursesGrid = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: auto;
  gap: 24px;
  margin-top: 120px;
  margin-bottom: 60px;
  align-items: end;
  z-index: 1000;
  
  @media (max-width: ${mobileBreakpoint}px) {
    grid-template-columns: 1fr;
  }
`;

const TagsContainer = styled.div`
  position: absolute;
  top: -220px;
  right: -20px;
  width: 300px;
  height: 300px;
  z-index: 10;
  pointer-events: none;
`;

const CurvyLine = styled.div`
  position: absolute;
  width: ${props => props.width || '100px'};
  height: ${props => props.height || '100px'};
  border: 2px solid ${props => props.color || '#CDFF9B'};
  border-radius: 50%;
  border-bottom-color: transparent;
  border-left-color: transparent;
  transform: rotate(${props => props.rotate || '0deg'});
  top: ${props => props.top || '0'};
  left: ${props => props.left || '0'};
`;

const DecorativeIcon = styled.div`
  position: absolute;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: ${props => props.bgColor || '#CDFF9B'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: black;
  font-size: 16px;
  z-index: 1;
  top: ${props => props.top || '0'};
  left: ${props => props.left || '0'};
`;

const PositionedTag = styled.div`
  position: absolute;
  top: ${props => props.top || '0'};
  left: ${props => props.left || '0'};
  transform: ${props => props.transform || 'none'};
  z-index: 2;
  pointer-events: auto;
`;

const LogoDecorator = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FloatingCardContainer = styled.div`
  position: absolute;
  width: 120px;
  right: ${props => props.right};
  top: ${props => props.top};
  transform: rotate(${props => props.rotate}deg);
  z-index: ${props => props.zIndex || 1};
`;

const CircleImage = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  object-position: center;
`;

// Mobile-specific components
const MobileHeader = styled.div`
  padding: 16px;
`;

const MobileHero = styled.div`
`;

const MobileCardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  
  
  > * {
    min-height: 0;
    height: auto;
    margin-top: auto;
  }
`;

const MobileCardsSectionTitle = styled.h2`
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 8px;
  margin-top: 60px;
  text-align: center;
  color: ${props => props.theme.colors.primary};
`;

const MobileCardsSectionSubtitle = styled.p`
  font-size: 18px;
  margin: 0 0 24px;
  text-align: center;
  color: ${props => props.theme.colors.primary};
  opacity: 0.9;
`;

const ZIndexProvider = styled.div`
  position: relative;
  z-index: ${props => props.zIndex + 1000 || 1000};
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  height: ${props => props.height || 'auto'};
`;

// Neue styled components für die Besitzerin erstellen
const OwnerSection = styled.section`
  position: relative;
  margin: 80px 0;
  display: flex;
  justify-content: space-between;
  
  @media (max-width: ${mobileBreakpoint}px) {
    flex-direction: column;
    align-items: center;
  }
`;

const OwnerImageContainer = styled.div`
  position: relative;
  width: 400px;
  height: 400px;
  border-radius: 50% 20% 40% 10%;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  border: 6px solid white;
  z-index: 2;
  
  @media (max-width: ${mobileBreakpoint}px) {
    width: 300px;
    height: 300px;
    margin-top: 40px;
  }
`;

const OwnerPicture = styled.picture`
  width: 100%;
  height: 100%;
  display: block;
`;

const OwnerImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`;

const OwnerInfo = styled.div`
  max-width: 550px;
  z-index: 1;
  padding-right: 20px;

  @media (max-width: 980px) {
    max-width: 350px;
    padding-right: 20px;
  }
  
  @media (max-width: ${mobileBreakpoint}px) {
    margin-bottom: 40px;
    padding-right: unset;
    text-align: center;
  }
`;

const OwnerName = styled.h2`
  font-size: 42px;
  margin-bottom: 16px;
  color: ${props => props.theme.colors.primary};
`;

const OwnerTitle = styled.h3`
  font-size: 24px;
  margin-bottom: 24px;
  font-weight: 600;
  color: ${props => props.theme.colors.secondary};
`;

const OwnerDescription = styled.p`
  font-size: 18px;
  line-height: 1.7;
  margin-bottom: 32px;
`;

const BackgroundShape = styled.div`
  position: absolute;
  width: 600px;
  height: 600px;
  background-color: ${props => props.theme.colors.accent2};
  opacity: 0.3;
  border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
  right: -90px;
  top: -40px;
  z-index: 0;
`;

const WhatMakesSpecialSection = styled.section`
  position: relative;
  margin: 80px 0 60px;
  z-index: 2;
`;

const WhatMakesSpecialTitle = styled.h2`
  font-size: 32px;
  margin: 0 0 8px;
  text-align: center;
  color: ${props => props.theme.colors.primary};
`;

const WhatMakesSpecialSubtitle = styled.p`
  font-size: 18px;
  margin: 0 0 40px;
  text-align: center;
  color: ${props => props.theme.colors.primary};
  opacity: 0.9;
`;

const WhatMakesSpecialGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  max-width: 900px;
  margin: 0 auto;

  @media (max-width: ${mobileBreakpoint}px) {
    grid-template-columns: 1fr;
    padding: 0 16px;
  }
`;

const WhatMakesSpecialCard = styled.div`
  background: white;
  border-radius: 24px;
  border: 2px solid ${props => props.$borderColor};
  padding: 28px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  box-sizing: border-box;
`;

const WhatMakesSpecialCardIcon = styled.img`
  width: 80px;
  height: 80px;
  object-fit: contain;
  margin-bottom: 16px;
`;

const WhatMakesSpecialCardTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 12px;
  color: ${props => props.theme.colors.primary};
`;

const WhatMakesSpecialCardDescription = styled.p`
  font-size: 15px;
  line-height: 1.5;
  margin: 0;
  color: ${props => props.theme.colors.primary};
  opacity: 0.9;
`;

const ReviewsSection = styled.section`
  position: relative;
  margin: 80px 0 60px;
  z-index: 2;
`;

const ReviewsSectionTitle = styled.h2`
  font-size: 32px;
  margin: 0 0 8px;
  text-align: center;
  color: ${props => props.theme.colors.primary};
`;

const ReviewsSectionSubtitle = styled.p`
  font-size: 18px;
  margin: 0 0 40px;
  text-align: center;
  color: ${props => props.theme.colors.primary};
  opacity: 0.9;
`;

const ReviewsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  max-width: 900px;
  margin: 0 auto;

  @media (max-width: ${mobileBreakpoint}px) {
    grid-template-columns: 1fr;
    padding: 0 16px;
  }
`;

const REVIEW_SHADOW_PADDING = 24;
const REVIEW_CAROUSEL_GAP = 28;

const ReviewsCarouselWrapper = styled.div`
  margin: 0 -${REVIEW_SHADOW_PADDING}px;
  padding: 0;
`;

const ReviewsCarouselTrack = styled.div`
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  gap: ${REVIEW_CAROUSEL_GAP}px;
  padding: 4px ${REVIEW_SHADOW_PADDING}px;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const ReviewSlide = styled.div`
  flex: 0 0 100%;
  min-width: 0;
  scroll-snap-align: center;
  scroll-snap-stop: always;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 15px;
`;

const ReviewsCarouselControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 5px;
  padding: 0 16px;
`;

const ReviewsCarouselArrow = styled.button`
  width: 60px;
  height: 60px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  flex-shrink: 0;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ReviewsCarouselArrowImg = styled.img`
  width: 60px;
  height: 60px;
  object-fit: contain;
  pointer-events: none;
`;

const ReviewsCarouselDots = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const ReviewsCarouselDot = styled.button`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  padding: 0;
  cursor: pointer;
  background: ${props => props.$active ? props.theme.colors.secondary : 'rgba(0,0,0,0.2)'};
  transition: background 0.2s, transform 0.2s;

  &:hover {
    background: ${props => props.$active ? props.theme.colors.secondary : 'rgba(0,0,0,0.35)'};
  }
`;

const ReviewCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`;

const ReviewQuote = styled.p`
  font-size: 15px;
  line-height: 1.6;
  margin: 0 0 16px;
  color: ${props => props.theme.colors.primary};
  font-style: italic;
  flex: 1;
`;

const ReviewAuthor = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.colors.secondary};
`;

const ExpertiseSection = styled.section`
  position: relative;
  margin: 80px 0 60px;
  z-index: 2;
  max-width: 1000px;
  margin-left: auto;
  margin-right: auto;
  padding: 0 24px;

  @media (max-width: ${mobileBreakpoint}px) {
    padding: 0 16px;
    margin: 60px 0 40px;
  }
`;

const ExpertiseInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 48px;

  @media (max-width: ${mobileBreakpoint}px) {
    flex-direction: column;
    gap: 32px;
    text-align: center;
  }
`;

const ExpertiseContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ExpertiseTitle = styled.h2`
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 16px;
  color: ${props => props.theme.colors.primary};
  line-height: 1.2;

  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 28px;
  }
`;

const ExpertiseDescription = styled.p`
  font-size: 18px;
  line-height: 1.6;
  margin: 0 0 32px;
  color: ${props => props.theme.colors.primary};
  opacity: 0.9;
  max-width: 480px;

  @media (max-width: ${mobileBreakpoint}px) {
    max-width: none;
    margin: 0 0 28px;
  }
`;

const ExpertiseStats = styled.div`
  display: flex;
  gap: 48px;
  margin-bottom: 28px;

  @media (max-width: ${mobileBreakpoint}px) {
    justify-content: center;
    gap: 40px;
    margin-bottom: 24px;
  }
`;

const ExpertiseStat = styled.div`
  display: flex;
  flex-direction: column;
`;

const ExpertiseStatLabel = styled.span`
  font-size: 15px;
  color: ${props => props.theme.colors.primary};
  opacity: 0.85;
  margin-bottom: 4px;
`;

const ExpertiseStatValue = styled.span`
  font-size: 42px;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  line-height: 1;

  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 36px;
  }
`;

const ExpertiseFeatures = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 16px 24px;

  @media (max-width: ${mobileBreakpoint}px) {
    justify-content: center;
  }
`;

const ExpertiseFeatureItem = styled.li`
  font-size: 15px;
  color: ${props => props.theme.colors.primary};
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '✓';
    color: ${props => props.theme.colors.secondary};
    font-weight: 700;
  }
`;

const ExpertiseImageContainer = styled.div`
  flex-shrink: 0;
  position: relative;
  width: 320px;
  height: 320px;

  @media (max-width: ${mobileBreakpoint}px) {
    width: 260px;
    height: 260px;
  }
`;

const ExpertiseBlob = styled.div`
  position: absolute;
  width: 120%;
  height: 120%;
  background-color: ${props => props.theme.colors.accent1};
  opacity: 0.35;
  border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
  top: -15px;
  right: -40px;
  z-index: 0;
`;

const ExpertiseImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 50% 20% 40% 10%;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  border: 6px solid white;
  z-index: 1;
`;

const ExpertisePicture = styled.picture`
  width: 100%;
  height: 100%;
  display: block;
`;

const ExpertiseImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`;

const TrialLessonCtaSection = styled.section`
  background-color: color-mix(in srgb, ${props => props.theme.colors.accent2} 100%, transparent);
  border-radius: 24px;
  padding: 28px 24px;
  margin: 80px auto 60px;
  margin-top: 130px;
  position: relative;
  max-width: 900px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  box-sizing: border-box;

  @media (max-width: ${mobileBreakpoint}px) {
    margin: 60px 16px 40px;
    margin-top: 100px;
    padding: 28px 20px;
  }
`;

const TrialLessonCtaTitle = styled.h2`
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 12px;
  color: ${props => props.theme.colors.primary};
  line-height: 1.2;

  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 24px;
  }
`;

const TrialLessonCtaSubtitle = styled.p`
  font-size: 18px;
  line-height: 1.5;
  margin: 0 0 24px;
  color: ${props => props.theme.colors.primary};
  opacity: 0.9;

  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 15px;
  }
`;

const TrialLessonCtaSparkleContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: visible;
  top: 0;
  left: 0;
`;

const TrialLessonCtaSparkle = styled.span`
  position: absolute;
  font-size: 10px;
  color: ${props => props.theme.colors.accent1};
  opacity: 0;
  animation: ${sparkle} 2s ease-in-out infinite;

  &:nth-child(1) {
    top: 20%;
    left: 15%;
    animation-delay: 0s;
  }

  &:nth-child(2) {
    top: 60%;
    right: 20%;
    animation-delay: 1s;
  }

  &:nth-child(3) {
    bottom: 25%;
    left: 25%;
    animation-delay: 0.5s;
  }
`;

const TrialLessonCtaButton = styled.button`
  padding: 16px 40px;
  background: linear-gradient(135deg, ${props => props.theme.colors.secondary} 0%, #e8316d 50%, ${props => props.theme.colors.secondary} 100%);
  background-size: 200% 200%;
  color: white;
  border: 2px solid ${props => props.theme.colors.accent1};
  border-radius: ${props => props.theme.borderRadius.medium};
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: visible;
  display: block;
  margin: 0 auto;
  box-shadow: 0 4px 20px rgba(219, 39, 98, 0.4), 0 0 20px rgba(241, 222, 124, 0.3);
  animation: ${glow} 3s ease-in-out infinite;

  &::before {
    content: '✨';
    position: absolute;
    font-size: 14px;
    top: -8px;
    left: 10px;
    animation: ${sparkle} 2s ease-in-out infinite;
    pointer-events: none;
  }

  &::after {
    content: '⭐';
    position: absolute;
    font-size: 12px;
    bottom: -6px;
    right: 15px;
    animation: ${sparkle} 2.5s ease-in-out infinite 0.5s;
    pointer-events: none;
  }

  &:hover {
    background-position: 100% 0;
    box-shadow: 0 8px 35px rgba(219, 39, 98, 0.5), 0 0 24px rgba(241, 222, 124, 0.4);
    transform: scale(1.01);
    animation: none;
  }

  &:active {
    transform: scale(0.98);
    box-shadow: 0 4px 20px rgba(219, 39, 98, 0.4);
  }

  @media (max-width: ${mobileBreakpoint}px) {
    width: 100%;
    font-size: 16px;
    padding: 14px 32px;
  }
`;

const SubtleBackgroundShape = styled.div`
  position: absolute;
  width: ${props => props.width || '200px'};
  height: ${props => props.height || '200px'};
  background-color: ${props => props.bgColor || props.theme.colors.accent1};
  opacity: 0.15;
  border-radius: ${props => props.borderRadius || '60% 40% 30% 70% / 60% 30% 70% 40%'};
  top: ${props => props.top || '0'};
  left: ${props => props.left || '0'};
  transform: rotate(${props => props.rotate || '0deg'});
  z-index: 0;
`;

const AUTO_SCROLL_INTERVAL_MS = 30000;

const Homepage = () => {
  const { isAuthenticated } = useAuth();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);
  const reviewsCarouselRef = useRef(null);
  const reviewsAutoScrollIntervalRef = useRef(null);

  const authed = isAuthenticated();
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  const reviews = texts.reviews ?? [];
  const reviewCount = reviews.length;
  const reviewPairs = [];
  if (reviewCount > 0) {
    if (reviewCount >= 1) reviewPairs.push([reviews[0]]);
    if (reviewCount >= 2) reviewPairs.push([reviews[1]]);
    for (let i = 2; i < reviews.length; i += 2) {
      reviewPairs.push(reviews.slice(i, i + 2));
    }
  }
  const slideCount = reviewPairs.length;

  const scrollReviewsToIndex = (slideIndex) => {
    const el = reviewsCarouselRef.current;
    if (!el || slideCount === 0) return;
    const i = Math.max(0, Math.min(slideIndex, slideCount - 1));
    const offset = REVIEW_SHADOW_PADDING;
    const slideWidth = el.clientWidth - REVIEW_SHADOW_PADDING * 2;
    const step = slideWidth + REVIEW_CAROUSEL_GAP;
    el.scrollTo({ left: offset + i * step, behavior: 'smooth' });
    setCurrentReviewIndex(i);
  };

  useEffect(() => {
    if (!isMobile) return;
    const el = reviewsCarouselRef.current;
    if (!el) return;

    const getStep = () => {
      const slideWidth = el.clientWidth - REVIEW_SHADOW_PADDING * 2;
      return slideWidth + REVIEW_CAROUSEL_GAP;
    };

    const scrollToNext = () => {
      const step = getStep();
      const offset = REVIEW_SHADOW_PADDING;
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (maxScroll <= 0 || step <= 0) return;
      if (el.scrollLeft >= maxScroll - 10) {
        el.scrollTo({ left: offset, behavior: 'smooth' });
      } else {
        el.scrollBy({ left: step, behavior: 'smooth' });
      }
    };

    const resetInterval = () => {
      if (reviewsAutoScrollIntervalRef.current) {
        clearInterval(reviewsAutoScrollIntervalRef.current);
      }
      reviewsAutoScrollIntervalRef.current = setInterval(scrollToNext, AUTO_SCROLL_INTERVAL_MS);
    };

    const handleScroll = () => {
      resetInterval();
      const step = getStep();
      if (step <= 0) return;
      const offset = REVIEW_SHADOW_PADDING;
      const index = Math.min(
        slideCount - 1,
        Math.max(0, Math.round((el.scrollLeft - offset) / step))
      );
      setCurrentReviewIndex(index);
    };

    resetInterval();
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      if (reviewsAutoScrollIntervalRef.current) clearInterval(reviewsAutoScrollIntervalRef.current);
      el.removeEventListener('scroll', handleScroll);
    };
  }, [isMobile, slideCount]);

  useEffect(() => {
    if (isMobile && currentReviewIndex >= slideCount && slideCount > 0) {
      setCurrentReviewIndex(0);
    }
  }, [isMobile, slideCount, currentReviewIndex]);

  // Preload owner images for faster loading
  useEffect(() => {
    const imgAvif = new Image();
    imgAvif.src = Images.ownerAvif;
    const imgWebp = new Image();
    imgWebp.src = Images.ownerWebp;
  }, []);

  useEffect(() => {
    const viewportWidth = window.innerWidth;
    fetch(config.getApiUrl(`/api/common/homepage?viewportWidth=${viewportWidth}`), { method: 'GET' })
      .catch(() => {});
  }, []);

  // Add favicon link to document head for SEO
  useEffect(() => {
    const link = document.querySelector("link[rel='icon']");
    if (!link) {
      const faviconLink = document.createElement('link');
      faviconLink.rel = 'icon';
      faviconLink.href = '/favicon.png';
      document.head.appendChild(faviconLink);
    }
  }, []);

  const mainDescription = texts.mainDescription;

  const handleOpenPopup = () => {
    setIsPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
  };

  // Decorative blobs config for Homepage
  const blobs = [
    {
      bg: theme.colors.accent1,
      width: '400px',
      height: '220px',
      opacity: 0.15,
      borderRadius: '40% 60% 60% 40% / 60% 40% 60% 40%',
      bottom: '80px',
      left: '-40px',
    },
    // Big red blob (desktop only)
    ...(!isMobile ? [{
      position: 'absolute',
      bg: theme.colors.secondary,
      width: '60vw',
      height: '700px',
      opacity: 0.15,
      radius: '30% 70% 20% 80% / 70% 40% 60% 30%',
      top: '400px',
      left: '0px',
      transform: 'rotate(25deg)',
    }] : []),
  ];

  if (isMobile) {
    return (
      <PageLayout blobs={blobs} onTerminClick={handleOpenPopup}>
        {/* All unique Homepage content here, excluding Header/Footer */}
        <MobileHero>
          <HeroTitle>
            <StarBadgeWithIcon />
            <MainTitle>Englisch</MainTitle>
          </HeroTitle>
          <Subtitle>Mit Mey</Subtitle>
          <Description>{mainDescription}</Description>
          
          {/* Mobile Owner Section */}
          <div style={{ position: 'relative', marginTop: '40px', marginBottom: '40px' }}>
            <div style={{ 
              width: '220px', 
              height: '220px', 
              margin: '0 auto',
              borderRadius: '50% 20% 40% 10%', 
              overflow: 'hidden',
              boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)',
              border: '6px solid white',
              position: 'relative',
              zIndex: 2
            }}>
              <picture>
                <source srcSet={Images.ownerAvif} type="image/avif" />
                <img 
                  src={Images.ownerWebp} 
                  width="220"
                  height="220"
                  loading="eager"
                  fetchPriority="high" 
                  alt="Michelle Mey Köhler - Gründerin von MeyTeaches" 
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </picture>
            </div>
            
            <div style={{ 
              textAlign: 'center', 
              marginTop: '20px',
              position: 'relative',
              zIndex: 2
            }}>
              <h2 style={{ 
                fontSize: '28px', 
                marginBottom: '8px', 
                color: theme.colors.primary 
              }}
                dangerouslySetInnerHTML={{ __html: texts.ownerName }}
              />
              <h3 style={{ 
                fontSize: '18px', 
                marginBottom: '16px', 
                color: theme.colors.secondary,
                fontWeight: 600
              }}>
                {texts.ownerTitle}
              </h3>
            </div>
            
            <div style={{
              position: 'absolute',
              width: '300px',
              height: '300px',
              backgroundColor: theme.colors.accent2,
              opacity: '0.3',
              borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
              top: '-40px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1
            }}></div>
          </div>
          
          {/* Probetermin Button - Between Owner and Cards */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center',
            margin: '30px 0 50px',
            position: 'relative',
            zIndex: 10
          }}>
            {!authed && <ProbeterminButton onClick={handleOpenPopup} />}
          </div>

          <WhatMakesSpecialSection>
            <WhatMakesSpecialTitle>{texts.whatMakesSpecialTitle}</WhatMakesSpecialTitle>
            <WhatMakesSpecialSubtitle>{texts.whatMakesSpecialSubtitle}</WhatMakesSpecialSubtitle>
            <WhatMakesSpecialGrid>
              {texts.whatMakesSpecialFeatures.map((feature, index) => {
                const config = [
                  { image: Images.together, borderColor: theme.colors.tertiary },
                  { image: Images.empathy, borderColor: theme.colors.accent3 },
                  { image: Images.idea, borderColor: theme.colors.accent1 },
                  { image: Images.experience, borderColor: theme.colors.secondary },
                ][index];
                return (
                  <WhatMakesSpecialCard key={feature.title} $borderColor={config.borderColor}>
                    <WhatMakesSpecialCardIcon src={config.image} alt="" />
                    <WhatMakesSpecialCardTitle>{feature.title}</WhatMakesSpecialCardTitle>
                    <WhatMakesSpecialCardDescription>{feature.description}</WhatMakesSpecialCardDescription>
                  </WhatMakesSpecialCard>
                );
              })}
            </WhatMakesSpecialGrid>
          </WhatMakesSpecialSection>

          <ReviewsSection>
            <ReviewsSectionTitle>{texts.reviewsSectionTitle}</ReviewsSectionTitle>
            <ReviewsSectionSubtitle>{texts.reviewsSectionSubtitle}</ReviewsSectionSubtitle>
            <ReviewsCarouselWrapper>
              <ReviewsCarouselTrack ref={reviewsCarouselRef}>
                {reviewPairs.map((pair, slideIndex) => (
                  <ReviewSlide key={slideIndex}>
                    {pair.map((review, i) => (
                      <ReviewCard key={i}>
                        <ReviewQuote>„{review.quote}"</ReviewQuote>
                        <ReviewAuthor>{review.author}</ReviewAuthor>
                      </ReviewCard>
                    ))}
                  </ReviewSlide>
                ))}
              </ReviewsCarouselTrack>
            </ReviewsCarouselWrapper>
            <ReviewsCarouselControls>
              <ReviewsCarouselArrow
                type="button"
                onClick={() => scrollReviewsToIndex(currentReviewIndex <= 0 ? slideCount - 1 : currentReviewIndex - 1)}
                disabled={slideCount <= 1}
                aria-label="Vorherige Bewertung"
              >
                <ReviewsCarouselArrowImg src={Images.leftArrow} alt="" />
              </ReviewsCarouselArrow>
              <ReviewsCarouselDots>
                {reviewPairs.map((_, index) => (
                  <ReviewsCarouselDot
                    key={index}
                    type="button"
                    $active={index === currentReviewIndex}
                    onClick={() => scrollReviewsToIndex(index)}
                    aria-label={`Seite ${index + 1}`}
                  />
                ))}
              </ReviewsCarouselDots>
              <ReviewsCarouselArrow
                type="button"
                onClick={() => scrollReviewsToIndex(currentReviewIndex >= slideCount - 1 ? 0 : currentReviewIndex + 1)}
                disabled={slideCount <= 1}
                aria-label="Nächste Bewertung"
              >
                <ReviewsCarouselArrowImg src={Images.rightArrow} alt="" />
              </ReviewsCarouselArrow>
            </ReviewsCarouselControls>
          </ReviewsSection>

          <ExpertiseSection>
            <ExpertiseInner>
              <ExpertiseContent>
                <ExpertiseTitle>{texts.expertiseSectionTitle}</ExpertiseTitle>
                <ExpertiseDescription>{texts.expertiseSectionDescription.replace('{years}', new Date().getFullYear() - (texts.expertiseYearsStart ?? 2013))}</ExpertiseDescription>
                <ExpertiseStats>
                  <ExpertiseStat>
                    <ExpertiseStatLabel>{texts.expertiseStatLessonsLabel}</ExpertiseStatLabel>
                    <ExpertiseStatValue>{texts.expertiseStatLessonsValue}</ExpertiseStatValue>
                  </ExpertiseStat>
                  <ExpertiseStat>
                    <ExpertiseStatLabel>{texts.expertiseStatStudentsLabel}</ExpertiseStatLabel>
                    <ExpertiseStatValue>{texts.expertiseStatStudentsValue}</ExpertiseStatValue>
                  </ExpertiseStat>
                </ExpertiseStats>
                <ExpertiseFeatures>
                  {texts.expertiseFeatures.map((feature, index) => (
                    <ExpertiseFeatureItem key={index}>{feature}</ExpertiseFeatureItem>
                  ))}
                </ExpertiseFeatures>
              </ExpertiseContent>
              <ExpertiseImageContainer>
                <ExpertiseBlob />
                <ExpertiseImageWrapper>
                  <ExpertisePicture>
                    <source srcSet={Images.ownerAvifAlternative} type="image/avif" />
                    <ExpertiseImage
                      src={Images.ownerWebpAlternative}
                      alt="Mey - Gründerin von MeyTeaches"
                      loading="lazy"
                    />
                  </ExpertisePicture>
                </ExpertiseImageWrapper>
              </ExpertiseImageContainer>
            </ExpertiseInner>
          </ExpertiseSection>

          <TrialLessonCtaSection>
            <TrialLessonCtaTitle>{texts.trialLessonCtaTitle}</TrialLessonCtaTitle>
            <TrialLessonCtaSubtitle>{texts.trialLessonCtaSubtitle}</TrialLessonCtaSubtitle>
            {!authed && (
              <TrialLessonCtaButton onClick={handleOpenPopup}>
                <TrialLessonCtaSparkleContainer>
                  <TrialLessonCtaSparkle>✨</TrialLessonCtaSparkle>
                  <TrialLessonCtaSparkle>⭐</TrialLessonCtaSparkle>
                  <TrialLessonCtaSparkle>✨</TrialLessonCtaSparkle>
                </TrialLessonCtaSparkleContainer>
                Kostenlose Probestunde
              </TrialLessonCtaButton>
            )}
          </TrialLessonCtaSection>

          <MobileCardsSectionTitle>{texts.mobileCoursesSectionTitle}</MobileCardsSectionTitle>
          <MobileCardsSectionSubtitle>{texts.mobileCoursesSectionSubtitle}</MobileCardsSectionSubtitle>
          <MobileCardsContainer>
            <Link to="/program#nachhilfe" style={{ textDecoration: 'none' }}>
              <CourseCard 
                title={texts.card_0_title}
                subtitle={texts.card_0_subtitle}
                color={theme.colors.tertiary}
                moveUpPixels={10}
                layout="horizontal"
                ageGroup="ab 6 Jahren"
                image={Images.kidDrawing}
                description={texts.card_0_Description}
              />
            </Link>
            <Link to="/program#sprachunterricht" style={{ textDecoration: 'none' }}>
              <CourseCard 
                title={texts.card_1_title}
                subtitle={texts.card_1_subtitle}
                color={theme.colors.accent3}
                ageGroup="ab 18 Jahren, nach Schulzeit"
                moveUpPixels={10}
                layout="horizontal"
                image={Images.kidsCreativeClass}
                description={texts.card_1_Description}
              />
            </Link>
            <Link to="/program#gruppenkurse" style={{ textDecoration: 'none' }}>
              <CourseCard 
                title={texts.card_2_title}
                subtitle={texts.card_2_subtitle}
                color={theme.colors.secondary}
                ageGroup="alle Altersklassen"
                moveUpPixels={10}
                layout="horizontal"
                image={Images.kidsLearning}
                description={texts.card_2_Description}
              />
            </Link>
          </MobileCardsContainer>
        </MobileHero>
        {!authed && (
          <Popup 
            isOpen={isPopupOpen} 
            onClose={handleClosePopup}
            title="Probestunde"
          />
        )}
      </PageLayout>
    );
  }

  return (
    <PageLayout blobs={blobs} onTerminClick={handleOpenPopup}>
      {/* All unique Homepage content here, excluding Header/Footer */}
      <HeroSection>
          <HeroContent>
            <HeroTitle>
              <StarBadgeWithIcon />
              <MainTitle>{texts.englishWithMey}</MainTitle>
            </HeroTitle>
            <Subtitle>{texts.withMey}</Subtitle>
            <Description>
              {mainDescription}
            </Description>
            {!authed && (
              <div style={{ marginTop: '80px', display: 'flex', justifyContent: 'center' }}>
                <ProbeterminButton onClick={handleOpenPopup} />
              </div>
            )}
          </HeroContent>
          
          <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <OwnerImageContainer>
              <OwnerPicture>
                <source srcSet={Images.ownerAvif} type="image/avif" />
                <OwnerImage 
                  src={Images.ownerWebp} 
                  alt="Mey - Gründerin von MeyTeaches"
                  width="450"
                  height="450"
                  loading="eager"
                  fetchPriority="high"
                />
              </OwnerPicture>
            </OwnerImageContainer>
            <div style={{ marginTop: '20px', textAlign: 'center', position: 'relative', zIndex: 2 }}>
              <OwnerName dangerouslySetInnerHTML={{ __html: texts.ownerName }} />
              <OwnerTitle style={{ marginBottom: 0 }}>{texts.ownerTitle}</OwnerTitle>
            </div>
            <BackgroundShape />
          </div>
        </HeroSection>

        <WhatMakesSpecialSection>
          <WhatMakesSpecialTitle>{texts.whatMakesSpecialTitle}</WhatMakesSpecialTitle>
          <WhatMakesSpecialSubtitle>{texts.whatMakesSpecialSubtitle}</WhatMakesSpecialSubtitle>
          <WhatMakesSpecialGrid>
            {texts.whatMakesSpecialFeatures.map((feature, index) => {
              const config = [
                { image: Images.together, borderColor: theme.colors.tertiary },
                { image: Images.empathy, borderColor: theme.colors.accent3 },
                { image: Images.idea, borderColor: theme.colors.accent1 },
                { image: Images.experience, borderColor: theme.colors.secondary },
              ][index];
              return (
                <WhatMakesSpecialCard key={feature.title} $borderColor={config.borderColor}>
                  <WhatMakesSpecialCardIcon src={config.image} alt="" />
                  <WhatMakesSpecialCardTitle>{feature.title}</WhatMakesSpecialCardTitle>
                  <WhatMakesSpecialCardDescription>{feature.description}</WhatMakesSpecialCardDescription>
                </WhatMakesSpecialCard>
              );
            })}
          </WhatMakesSpecialGrid>
        </WhatMakesSpecialSection>

        <ReviewsSection>
          <ReviewsSectionTitle>{texts.reviewsSectionTitle}</ReviewsSectionTitle>
          <ReviewsSectionSubtitle>{texts.reviewsSectionSubtitle}</ReviewsSectionSubtitle>
          <ReviewsGrid>
            {texts.reviews.map((review, index) => (
              <ReviewCard key={index}>
                <ReviewQuote>„{review.quote}"</ReviewQuote>
                <ReviewAuthor>{review.author}</ReviewAuthor>
              </ReviewCard>
            ))}
          </ReviewsGrid>
        </ReviewsSection>

        <ExpertiseSection>
          <ExpertiseInner>
            <ExpertiseContent>
              <ExpertiseTitle>{texts.expertiseSectionTitle}</ExpertiseTitle>
              <ExpertiseDescription>{texts.expertiseSectionDescription.replace('{years}', new Date().getFullYear() - (texts.expertiseYearsStart ?? 2013))}</ExpertiseDescription>
              <ExpertiseStats>
                <ExpertiseStat>
                  <ExpertiseStatLabel>{texts.expertiseStatLessonsLabel}</ExpertiseStatLabel>
                  <ExpertiseStatValue>{texts.expertiseStatLessonsValue}</ExpertiseStatValue>
                </ExpertiseStat>
                <ExpertiseStat>
                  <ExpertiseStatLabel>{texts.expertiseStatStudentsLabel}</ExpertiseStatLabel>
                  <ExpertiseStatValue>{texts.expertiseStatStudentsValue}</ExpertiseStatValue>
                </ExpertiseStat>
              </ExpertiseStats>
              <ExpertiseFeatures>
                {texts.expertiseFeatures.map((feature, index) => (
                  <ExpertiseFeatureItem key={index}>{feature}</ExpertiseFeatureItem>
                ))}
              </ExpertiseFeatures>
            </ExpertiseContent>
            <ExpertiseImageContainer>
              <ExpertiseBlob />
              <ExpertiseImageWrapper>
                <ExpertisePicture>
                  <source srcSet={Images.ownerAvifAlternative} type="image/avif" />
                  <ExpertiseImage
                    src={Images.ownerWebpAlternative}
                    alt="Mey - Gründerin von MeyTeaches"
                    loading="lazy"
                  />
                </ExpertisePicture>
              </ExpertiseImageWrapper>
            </ExpertiseImageContainer>
          </ExpertiseInner>
        </ExpertiseSection>

        <TrialLessonCtaSection>
          <TrialLessonCtaTitle>{texts.trialLessonCtaTitle}</TrialLessonCtaTitle>
          <TrialLessonCtaSubtitle>{texts.trialLessonCtaSubtitle}</TrialLessonCtaSubtitle>
          {!authed && (
            <TrialLessonCtaButton onClick={handleOpenPopup}>
              <TrialLessonCtaSparkleContainer>
                <TrialLessonCtaSparkle>✨</TrialLessonCtaSparkle>
                <TrialLessonCtaSparkle>⭐</TrialLessonCtaSparkle>
                <TrialLessonCtaSparkle>✨</TrialLessonCtaSparkle>
              </TrialLessonCtaSparkleContainer>
              Kostenlose Probestunde buchen
            </TrialLessonCtaButton>
          )}
        </TrialLessonCtaSection>
        
        <div style={{
          position: 'relative', 
          height: '180px',
          marginBottom: '-100px',
          marginTop: '100px',
          zIndex: 10,
          pointerEvents: 'none'
        }}>
          <CurvyLine width="260px" height="160px" color={theme.colors.tertiary} top="20px" left="calc(100% - 320px)" rotate="30deg" />
          <CurvyLine width="180px" height="100px" color={theme.colors.tertiary} top="60px" left="calc(100% - 350px)" rotate="-20deg" />
          
          <DecorativeIcon bgColor={theme.colors.accent1} top="120px" left="calc(100% - 150px)">★</DecorativeIcon>
          <DecorativeIcon bgColor={theme.colors.tertiary} top="20px" left="calc(100% - 80px)">✎</DecorativeIcon>
          <DecorativeIcon bgColor={theme.colors.secondary} top="100px" left="calc(100% - 120px)">♥</DecorativeIcon>
          
          <div style={{ position: 'absolute', top: '40px', left: 'calc(100% - 350px)', pointerEvents: 'auto' }}>
            <Tag label={texts.drawing} color={theme.colors.secondary} />
          </div>
          <div style={{ position: 'absolute', top: '120px', left: 'calc(100% - 280px)', pointerEvents: 'auto' }}>
            <Tag label={texts.artwork} color={theme.colors.accent3} />
          </div>
          <div style={{ position: 'absolute', top: '60px', left: 'calc(100% - 180px)', pointerEvents: 'auto' }}>
            <Tag label={texts.writing} color={theme.colors.tertiary} />
          </div>
          <CurvyLine 
            width="120px" 
            height="120px" 
            color={theme.colors.accent1} 
            top="140px" 
            left="calc(100% - 220px)" 
            rotate="120deg" 
          />
        </div>
        
        <CoursesGrid>
          <ZIndexProvider zIndex={2} height="calc(100% + 0px)" style={{ gridColumn: '1 / 2' }}>
            <Link to="/program#nachhilfe" style={{ textDecoration: 'none' }}>
              <CourseCard 
                title={texts.card_0_title}
                subtitle={texts.card_0_subtitle}
                color={theme.colors.tertiary}
                ageGroup="ab 6 Jahren"
                hasImage={true}
                layout="horizontal"
                moveUpPixels={10}
                image={Images.kidDrawing}
                description={texts.card_0_Description}
              />
            </Link>
          </ZIndexProvider>
          
          <ZIndexProvider zIndex={3} height="calc(100% + 50px)" style={{ gridColumn: '2 / 3' }}>
            <Link to="/program#sprachunterricht" style={{ textDecoration: 'none' }}>
              <CourseCard 
                title={texts.card_1_title}
                subtitle={texts.card_1_subtitle}
                color={theme.colors.accent3}
                layout="horizontal"
                hasImage={true}
                moveUpPixels={40}
                ageGroup="ab 18 Jahren, nach Schulzeit"
                image={Images.kidsCreativeClass}
                description={texts.card_1_Description}
              />
            </Link>
          </ZIndexProvider>
          
          <ZIndexProvider zIndex={1} style={{ gridColumn: '3 / 4' }}>
            <Link to="/program#gruppenkurse" style={{ textDecoration: 'none' }}>
              <CourseCard 
                title={texts.card_2_title}
                subtitle={texts.card_2_subtitle}
                color={theme.colors.secondary}
                ageGroup="alle Altersklassen"
                hasImage={true}
                layout="horizontal"
                moveUpPixels={0}
                image={Images.kidsLearning}
                description={texts.card_2_Description}
              />
            </Link>
          </ZIndexProvider>
        </CoursesGrid>
        
        {!authed && (
          <Popup 
            isOpen={isPopupOpen} 
            onClose={handleClosePopup}
            title="Probestunde"
          />
        )}
      </PageLayout>
  );
};

export default Homepage; 